import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Contact from "@/components/Contact";
import SEOHead from "@/components/SEOHead";
import NotFound from "@/pages/NotFound";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getServicePage, servicePages } from "@/data/servicePages";
import { games } from "@/data/games";

const ServicePage = () => {
  const { pathname } = useLocation();
  const page = getServicePage(pathname.replace(/^\//, "").replace(/\/$/, ""));

  if (!page) return <NotFound />;

  const baseUrl = "https://playfieldvr.nl";
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: page.h1,
        serviceType: "VR teambuilding op locatie",
        description: page.description,
        provider: {
          "@type": "LocalBusiness",
          name: "PlayfieldVR",
          url: baseUrl,
          telephone: "+31645441448",
        },
        areaServed: page.city
          ? { "@type": "City", name: page.city }
          : { "@type": "Country", name: "Nederland" },
        url: `${baseUrl}/${page.slug}`,
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  };

  const otherPages = servicePages.filter((p) => p.slug !== page.slug);
  const highlightedGames = games.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={page.title}
        description={page.description}
        canonicalPath={`/${page.slug}`}
        structuredData={structuredData}
      />
      <Navigation />

      <main>
        <article className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar Home
            </Link>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6">
              {page.h1}
            </h1>

            <p className="text-lg text-muted-foreground mb-10">{page.intro}</p>

            <ul className="grid gap-3 mb-12">
              {page.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-foreground">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{bullet}</span>
                </li>
              ))}
            </ul>

            {page.sections.map((section) => (
              <section key={section.heading} className="mb-10">
                <h2 className="text-2xl font-semibold text-foreground mb-3">
                  {section.heading}
                </h2>
                <p className="text-muted-foreground">{section.body}</p>
              </section>
            ))}

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                Games die we meenemen
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {highlightedGames.map((game) => (
                  <Link
                    key={game.slug}
                    to={`/experiences/${game.slug}`}
                    className="group rounded-lg border border-border overflow-hidden bg-card/50 hover:border-primary/60 transition-colors"
                  >
                    <img
                      src={game.image}
                      alt={`${game.title} VR game van PlayfieldVR`}
                      loading="lazy"
                      className="w-full h-32 object-cover"
                    />
                    <span className="block p-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      {game.title}
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to="/experiences"
                className="inline-flex items-center gap-2 text-primary hover:underline mt-4"
              >
                Bekijk alle VR games
                <ArrowRight className="w-4 h-4" />
              </Link>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                Veelgestelde vragen
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {page.faq.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="border-border">
                    <AccordionTrigger className="text-left text-base sm:text-lg text-foreground hover:text-primary hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <section className="mb-12 p-6 rounded-lg border border-border bg-card/50">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Vraag een prijsindicatie aan
              </h2>
              <p className="text-muted-foreground mb-4">
                Laat weten wanneer, waar en met hoeveel mensen. Je ontvangt doorgaans
                binnen één werkdag een voorstel op maat.
              </p>
              <Button asChild>
                <a href="#contact-form">Neem contact op</a>
              </Button>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                Ook interessant
              </h2>
              <ul className="space-y-2">
                {otherPages.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/${other.slug}`}
                      className="text-primary hover:underline"
                    >
                      {other.h1}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </article>

        <Contact />
      </main>

      <Footer />
    </div>
  );
};

export default ServicePage;
